import React, { useMemo } from "react"
import { getFeedBacks, getLecturer, getTechnology } from "../../lib/prismic/mapData"
import { NextSeo } from "next-seo"
import { webUrl } from "../../config/web"
import { Layout } from "../Layout/Layout"
import { SinglePage } from "../SinglePage/SinglePage"
import { css } from "@emotion/react"
import { Section } from "../Section"
import { Button, Col, List, Row, Space, Typography } from "antd"
import Link from "next/link"
import Image from "next/image"
import { Comments } from "../Comments/Comments"
import { Course, getFeedbackAvatarUrl } from "../../lib/prismic/courses"
import { CommonArticles, H2, H4 } from "../blog/common"
import { CoursesList } from "../CoursesList/CoursesList"
import { PrismicDocument } from "@prismicio/types"
import StandardArticle from "../blog/StandardArticle"
import { CourseAgeGroup } from "../../lib/prismic/Course"
import styled from "@emotion/styled"
import { useCourseCapacityStatus } from "../../lib/supabase/useCourseCapacityStatus"
import { getCourse } from "../../lib/prismic/getCourse"

export const CoursesSlugPage = ({ slug, document, blogPostsWithSameTag, openSimilarCourses }: Props) => {
  const course = useMemo(() => document?.course && getCourse(document.course), [document])
  const capacityData = useMemo(() => course && document && { capacity: course.capacity, courseSlug: course.slug }, [course, document])
  const courseCapacityStatus = useCourseCapacityStatus((course && document && capacityData) || undefined)

  if (!document || !document.authorWithFeedbacks || !document.course) {
    return <>nenalezeno</>
  }
  const lecturer = getLecturer(document.authorWithFeedbacks)
  const feedbacks = getFeedBacks(document.authorWithFeedbacks?.feedbacks)
  const technology = getTechnology(document.course)
  const courseStringType = course?.ageGroup === CourseAgeGroup.adult ? "kurzy" : "krouzky"

  if (!course) {
    return null
  }

  return (
    <>
      <NextSeo
        title={`${course.name} | ${course.descriptionType}`}
        description={course.excerpt?.toString()}
        canonical={`${webUrl}/${courseStringType}/${slug}`}
        openGraph={{
          url: `${webUrl}/${courseStringType}/${slug}`,
          title: `${course.name} | ${course.descriptionType}`,
          description: course.excerpt?.toString(),
          ...(course.image && {
            images: [
              {
                url: course.image.url,
                width: course.image.dimensions.width,
                height: course.image.dimensions.height,
                alt: course.image.alt ?? slug
              }
            ]
          })
        }}
      />

      <Layout>
        <SinglePage title={course.name} color={technology?.color}>
          <Section
            css={css`
              padding-top: 20px;
            `}>
            <Space direction="vertical" size="large">
              <div
                css={css`
                  background-color: #fff;
                  border-radius: 20px;
                  padding: 20px;
                `}>
                {course.content}
              </div>

              <Link href={`/registrace/${slug}`}>
                <Button type="primary">Přejít na registraci</Button>
              </Link>

              <div
                css={css`
                  margin-bottom: 20px;
                `}>
                <H4 level={4}>Základní informace</H4>
                <List
                  size="small"
                  css={css`
                    padding: 15px;
                    background-color: #fff;
                    border-radius: 20px;
                  `}>
                  <ListItem>
                    <Typography.Title level={5}>Kdy?</Typography.Title>
                    <Typography.Text style={{ fontSize: 12 }}>{course.when}</Typography.Text>
                  </ListItem>
                  <ListItem>
                    <Typography.Title level={5}>Kde?</Typography.Title>
                    <Typography.Text style={{ fontSize: 12 }}>{course.where}</Typography.Text>
                  </ListItem>
                  <ListItem>
                    <Typography.Title level={5}>Cena</Typography.Title>
                    <Typography.Text style={{ fontSize: 12 }}>{`${course.price} Kč`}</Typography.Text>
                  </ListItem>
                  {course.additionalInformation && course.additionalInformation !== "" && (
                    <List.Item>
                      <Typography.Text style={{ fontSize: 12 }}>{course.additionalInformation}</Typography.Text>
                    </List.Item>
                  )}
                </List>
              </div>

              {lecturer && (
                <div
                  css={css`
                    margin-bottom: 20px;
                  `}>
                  <H4 level={4}>Hodnocení</H4>
                  <Row
                    align="middle"
                    gutter={8}
                    css={css`
                      padding: 15px;
                      background-color: #fff;
                      border-radius: 20px;
                    `}>
                    <Col>
                      {feedbacks?.length > 0 && (
                        <div
                          css={css`
                            margin-bottom: 20px;
                          `}>
                          <div
                            css={css`
                              margin-bottom: 20px;
                            `}>
                            <Comments
                              commentStyle={{ fontSize: 12 }}
                              css={css`
                                padding: 0 15px 0 15px;
                              `}
                              comments={feedbacks.slice(0, 6).map((f, i) => ({
                                dateTime: f.dateTime,
                                avatarImagePath: f.imageUrl || getFeedbackAvatarUrl(f.sex, i),
                                content: f.description,
                                author: f.name
                              }))}
                            />
                          </div>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              )}
            </Space>
          </Section>

          <Section>
            {openSimilarCourses && openSimilarCourses.length > 0 && (
              <div>
                <CommonArticles>
                  <Typography.Title level={3}>Nabídka dalších kroužků a kurzů</Typography.Title>

                  <CoursesList
                    courses={openSimilarCourses || []}
                    css={css`
                      margin: 20px 0 0 0;
                    `}
                  />
                </CommonArticles>
              </div>
            )}
          </Section>

          <Section>
            {blogPostsWithSameTag && blogPostsWithSameTag.length > 0 && (
              <div>
                <CommonArticles>
                  <H2>Články z našeho blogu</H2>
                  {blogPostsWithSameTag.map((d: PrismicDocument) => (
                    <StandardArticle key={d.id} document={d} />
                  ))}
                </CommonArticles>
              </div>
            )}
          </Section>
        </SinglePage>
      </Layout>
    </>
  )
}

const ImageRounded = styled(Image)`
  border-radius: 50%;
  overflow: hidden;
`

const ListItem = styled(List.Item)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  > h5 {
    margin-bottom: 0.25em;
  }
`

interface Props {
  id?: string
  slug: string
  document: Course | null
  blogPostsWithSameTag: PrismicDocument<any>[] | null
  openSimilarCourses: PrismicDocument<any>[] | null
}
